import {
  Button,
  InputBase,
  InputAdornment,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Building2, User } from "lucide-react";
import Logo from "../reusables/Logo";
import { setTokens, getAccessToken } from "../../utils/authTokens";
import { fetchAndSetOrganization } from "../../utils/organizationService";
import { syncPostHogOrganization } from "../../utils/posthogAnalytics";
import { brand } from "../../theme/brandTokens";

const AcceptInvite = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [alreadyAcceptedOrgId, setAlreadyAcceptedOrgId] = useState(null);
  const hasRedirectedRef = useRef(false);

  // Verify invite on load via public endpoint (no auth). Handles valid/expired and already-accepted.
  useEffect(() => {
    if (!token) {
      setFetchError("Invalid or missing invite link.");
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "user/verify-invite",
          { invite_token: token }
        );
        const data = res.data;
        if (!data.valid) {
          setFetchError(data.message || "Invalid or expired invite link.");
          return;
        }
        if (data.already_accepted) {
          setAlreadyAccepted(true);
          setAlreadyAcceptedOrgId(data.organization_id);
          return;
        }
        setInviteData({
          user_id: data.user_id,
          organization_id: data.organization_id,
          type: data.type,
          justCreated: true,
          orgName: data.org_name || "this organization",
          inviteeEmail: data.email || "",
          inviteeFirstName: data.first_name || "",
          inviteeLastName: data.last_name || "",
          inviterName: data.inviter_name || null,
          inviterEmail: data.inviter_email || null,
          alreadyJoined: false,
          fullOrg: null,
          fullUser: null,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          password: "",
          agreedToTerms: false,
        });
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not verify invite link.";
        setFetchError(message);
      }
    };
    verify();
  }, [token]);

  // When invite was already accepted: redirect to app if user has session, else to login.
  useEffect(() => {
    if (!alreadyAccepted || !alreadyAcceptedOrgId || hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    const loginRedirectUrl = `/login?redirect=${encodeURIComponent(`/accept-invite/${token}`)}`;
    if (getAccessToken()) {
      fetchAndSetOrganization(alreadyAcceptedOrgId)
        .then(() => navigate("/dashboard/"))
        .catch(() => navigate(loginRedirectUrl));
    } else {
      navigate(loginRedirectUrl);
    }
  }, [alreadyAccepted, alreadyAcceptedOrgId, navigate, token]);

  const acceptInvite = async () => {
    if (!inviteData) return;
    setErrorMsg(null);
    setLoader(true);
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "update-user",
        {
          user_id: inviteData.user_id,
          organization_id: inviteData.organization_id,
          type: inviteData.type,
          status: "active",
        }
      );
      localStorage.setItem(
        "organization",
        JSON.stringify(res.data.updatedItem)
      );

      const relatedOrgs = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "add-related-org",
        {
          user_id: inviteData.user_id,
          organization_name: res.data.updatedItem.organization_name.S,
          organization_id: res.data.updatedItem.orgId.S,
        }
      );
      localStorage.setItem("user", JSON.stringify(relatedOrgs.data.user));

      const userRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-user-by-id",
        { userId: inviteData.user_id }
      );
      localStorage.setItem("user", JSON.stringify(userRes.data.user));

      navigate("/dashboard/");
    } catch (error) {
      // Log for production diagnostics when accept invite (join) fails
      console.error("AcceptInvite: accept invite failed", error.response?.data || error.message || error);
      if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } finally {
      setLoader(false);
    }
  };

  const handleJoinOrganization = async () => {
    await acceptInvite();
  };

  const handleGoToDashboard = () => {
    if (!inviteData?.fullOrg) return;
    localStorage.setItem("organization", JSON.stringify(inviteData.fullOrg));
    syncPostHogOrganization(inviteData.fullOrg);
    navigate("/dashboard/");
  };

  const handleCreateAccountAndJoin = async () => {
    if (!inviteData) return;
    setErrorMsg(null);

    if (!inviteData.agreedToTerms) {
      setErrorMsg("Please accept the Terms of Service and Privacy Policy.");
      return;
    }
    if (!inviteData.password?.trim()) {
      setErrorMsg("Please enter a password.");
      return;
    }

    setLoader(true);
    try {
      // Update name if user changed it (backend expects full user payload)
      if (
        inviteData.first_name !== inviteData.inviteeFirstName ||
        inviteData.last_name !== inviteData.inviteeLastName
      ) {
        await axios.post(
          process.env.REACT_APP_BACKEND_URL + "user/update-user",
          {
            user_id: inviteData.user_id,
            email: inviteData.inviteeEmail,
            first_name: inviteData.first_name,
            last_name: inviteData.last_name,
            google: false,
          }
        );
      }

      const changePasswordRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "user/change-password",
        {
          reset_token: token,
          newPassword: inviteData.password,
        }
      );

      // Store tokens so subsequent update-user / add-related-org have Bearer auth (like ResetPassword)
      const access_token = changePasswordRes?.data?.access_token;
      if (access_token) {
        setTokens({
          access_token,
          refresh_token: changePasswordRes.data.refresh_token,
          expires_in: changePasswordRes.data.expires_in,
        });
      } else if (inviteData.inviteeEmail) {
        const loginRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "get-user",
          { email: inviteData.inviteeEmail, password: inviteData.password }
        );
        if (loginRes.data?.access_token) {
          setTokens({
            access_token: loginRes.data.access_token,
            refresh_token: loginRes.data.refresh_token,
            expires_in: loginRes.data.expires_in,
          });
        }
      }

      await acceptInvite();
    } catch (error) {
      // Log for production diagnostics when create account and join fails
      console.error("AcceptInvite: create account and join failed", error.response?.data || error.message || error);
      if (error.response?.data?.message) {
        setErrorMsg(error.response.data.message);
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
      setLoader(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const updateInviteData = (updates) => {
    setInviteData((prev) => (prev ? { ...prev, ...updates } : null));
    setErrorMsg(null);
  };

  // Loading or invalid token
  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">{fetchError}</h2>
          <p className="mt-2 text-sm text-gray-600">
            You can request a new invite from your team or contact support.
          </p>
          <Button
            sx={{ mt: 3, textTransform: "none" }}
            onClick={() => navigate("/login")}
          >
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  if (alreadyAccepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12">
        <div className="flex justify-center">
          <CircularProgress />
        </div>
        <p className="text-center text-sm text-gray-600 mt-4">Redirecting...</p>
      </div>
    );
  }

  if (!inviteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12">
        <div className="flex justify-center">
          <CircularProgress />
        </div>
        <p className="text-center text-sm text-gray-600 mt-4">Verifying invite...</p>
      </div>
    );
  }

  const isNewUser = inviteData.justCreated;
  const inviterText =
    inviteData.inviterName && inviteData.inviterEmail
      ? `${inviteData.inviterName} (${inviteData.inviterEmail}) has invited you to join their organization.`
      : "You've been invited to join their organization.";

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  })();
  const currentUserId = currentUser?.userId?.S ?? currentUser?.userId ?? null;
  const isCorrectUser = currentUserId && String(currentUserId) === String(inviteData.user_id);
  const needsLogin =
    inviteData.alreadyJoined || !inviteData.justCreated;
  const showSignInGate =
    needsLogin && (!currentUser || !isCorrectUser);
  const loginRedirectUrl = `/login?redirect=${encodeURIComponent(`/accept-invite/${token}`)}`;

  // ——— Sign-in required (existing user or already joined, but not logged in or wrong user) ———
  if (showSignInGate) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 text-center">
            <div className="flex justify-center">
              <Building2 className="h-12 w-12 text-brand-primary" />
            </div>
            <h2 className="mt-6 text-xl font-bold text-gray-900">
              Sign in to continue
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {currentUser && !isCorrectUser ? (
                <>
                  This invitation was sent to{" "}
                  <span className="font-medium text-gray-900">
                    {inviteData.inviteeEmail}
                  </span>
                  . Please sign in with that account to accept this invitation.
                </>
              ) : (
                <>
                  Sign in to accept your invitation to{" "}
                  <span className="font-medium text-gray-900">
                    {inviteData.orgName}
                  </span>
                  .
                </>
              )}
            </p>
            <Button
              component={Link}
              to={loginRedirectUrl}
              fullWidth
              variant="contained"
              color="primary"
              sx={{
                mt: 5,
                py: "12px",
                borderRadius: "6px",
                fontSize: "16px",
              }}
            >
              Sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ——— Step 1: Invite preview ———
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10">
            {inviteData.alreadyJoined ? (
              <>
                <div className="flex justify-center">
                  <Building2 className="h-12 w-12 text-brand-primary" />
                </div>
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                  You're already a member of {inviteData.orgName}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  You've already joined this organization. Sign in to go to the dashboard.
                </p>
                <Button
                  component={Link}
                  to={loginRedirectUrl}
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    mt: 6,
                    py: "12px",
                    borderRadius: "6px",
                    fontSize: "16px",
                  }}
                >
                  Sign in to go to dashboard
                </Button>
              </>
            ) : (
              <>
                <div className="flex justify-center">
                  <Building2 className="h-12 w-12 text-brand-primary" />
                </div>
                <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
                  You're invited to join {inviteData.orgName}
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                  {inviterText}
                </p>

                <div className="mt-6 rounded-lg bg-gray-100 p-4 space-y-3">
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Organization</span>
                    <span className="font-medium">{inviteData.orgName}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <span>Email</span>
                    <span className="font-medium">{inviteData.inviteeEmail}</span>
                  </div>
                </div>

                {err_msg && (
                  <div className="mt-4 rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <p className="ml-3 text-sm text-red-700">{err_msg}</p>
                    </div>
                  </div>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loader}
                  onClick={() => {
                    if (isNewUser) {
                      setStep(2);
                    } else {
                      handleJoinOrganization();
                    }
                  }}
                  sx={{
                    mt: 4,
                    py: "12px",
                    borderRadius: "6px",
                    fontSize: "16px",
                  }}
                >
                  {loader ? (
                    <CircularProgress size="1.25rem" sx={{ color: "white" }} />
                  ) : isNewUser ? (
                    "Create Account & Join"
                  ) : (
                    "Join Organization"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ——— Step 2: Create account (new users only) ———
  const inputSx = {
    bgcolor: "white",
    border: "1px solid #D1D5DB",
    width: "100%",
    px: 2,
    py: "6px",
    borderRadius: "6px",
    fontSize: "14px",
    "&:focus": { outline: "none", borderColor: brand.primary },
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white shadow sm:rounded-xl py-6 px-4 sm:px-8">
          {/* Header inside card */}
          <div className="flex justify-center">
            <User className="h-10 w-10 text-brand-primary" />
          </div>
          <h2 className="mt-4 text-center text-xl font-bold text-gray-900">
            Create Your Account
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Set up your account to join {inviteData.orgName}.
          </p>

          <div className="mt-5 space-y-4">
            {err_msg && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <p className="ml-3 text-sm text-red-700">{err_msg}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-0.5">
                  First Name
                </label>
                <InputBase
                  placeholder="First name"
                  value={inviteData.first_name}
                  onChange={(e) => updateInviteData({ first_name: e.target.value })}
                  disabled={loader}
                  sx={inputSx}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-0.5">
                  Last Name
                </label>
                <InputBase
                  placeholder="Last name"
                  value={inviteData.last_name}
                  onChange={(e) => updateInviteData({ last_name: e.target.value })}
                  disabled={loader}
                  sx={inputSx}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-0.5">
                Email Address
              </label>
              <InputBase
                value={inviteData.inviteeEmail}
                disabled
                sx={{
                  ...inputSx,
                  bgcolor: "#F9FAFB",
                  color: "#6B7280",
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-0.5">
                Password
              </label>
              <div className="relative">
                <InputBase
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={inviteData.password}
                  onChange={(e) => updateInviteData({ password: e.target.value })}
                  disabled={loader}
                  sx={{
                    ...inputSx,
                    pr: "40px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleTogglePassword}
                  disabled={loader}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <VisibilityOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Visibility className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                color="primary"
                checked={inviteData.agreedToTerms}
                onChange={(e) =>
                  updateInviteData({ agreedToTerms: e.target.checked })
                }
                disabled={loader}
                sx={{
                  pt: 0,
                  pb: 0,
                  mt: -0.5,
                }}
              />
              <label className="flex-1 min-w-0 text-sm text-gray-700 cursor-pointer leading-snug select-none">
                I agree to the{" "}
                <a
                  href="https://twilanswer.com/terms-and-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand-primary hover:text-brand-hover underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://twilanswer.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand-primary hover:text-brand-hover underline"
                >
                  Privacy Policy
                </a>
                .
              </label>
            </div>

            <Button
              fullWidth
              disabled={loader || !inviteData.password?.trim() || !inviteData.agreedToTerms}
              onClick={handleCreateAccountAndJoin}
              variant="contained"
              color="primary"
              sx={{
                py: "10px",
                borderRadius: "6px",
                fontSize: "15px",
                "&:disabled": { opacity: 0.6 },
              }}
            >
              {loader ? (
                <CircularProgress size="1.25rem" sx={{ color: "white" }} />
              ) : (
                "Create Account & Join Organization"
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep(1)}
              disabled={loader}
              className="w-full text-sm text-gray-600 hover:text-gray-900 pt-1"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
